import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import logger from 'src/util/logger';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactComponentElement} - AdminPrimaryBillingPointList component
 */
function AdminPrimaryBillingPointList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.log(error); },
    );
  };

  const intl = useIntl();

  // Loading state
  const loadingText = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.loading',
    defaultMessage: 'Loading...',
  });

  if (!props) {
    return <div>{loadingText}</div>;
  }

  const { relay, viewer } = props;
  const { primaryBillingPoints } = viewer;

  // Breadcrumb names
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.breadcrumb.admin',
    defaultMessage: 'Admin',
  });
  const breadcrumbPrimaryBillingPoints = intl.formatMessage({
    id: 'admin.breadcrumb.primary_billing_points',
    defaultMessage: 'Primary Billing Points',
  });

  // Page title and headings
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.page_title',
    defaultMessage: 'Enosi Admin - Primary Billing Points',
  });
  const pageHeading = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.page_heading',
    defaultMessage: 'Primary Billing Points',
  });

  // Table headers - Primary Billing Point section
  const headerPrimaryBillingPoint = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.primary_billing_point',
    defaultMessage: 'Primary Billing Point',
  });
  const headerActive = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.active',
    defaultMessage: 'Active',
  });
  const headerIdentifier = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.identifier',
    defaultMessage: 'Identifier',
  });
  const headerId = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.id',
    defaultMessage: 'ID',
  });
  const headerExtId = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.external_identifier',
    defaultMessage: 'Ext ID',
  });
  const tooltipExtId = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.tooltip.ext_id',
    defaultMessage: 'External identifier',
  });

  // Table headers - Property section
  const headerProperty = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.property',
    defaultMessage: 'Property',
  });
  const headerTitle = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.title',
    defaultMessage: 'Title',
  });

  // Load more button text
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more primary billing points to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPrimaryBillingPoints },
        ]}
      />

      <h1>{pageHeading}</h1>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col" colSpan={4} className="text-center">{headerPrimaryBillingPoint}</th>
                <th scope="col" colSpan={3} className="text-center">{headerProperty}</th>
              </tr>
              <tr>
                <th scope="col">{headerActive}</th>
                <th scope="col">{headerIdentifier}</th>
                <th scope="col">{headerId}</th>
                <th scope="col">
                  <abbr title={tooltipExtId}>{headerExtId}</abbr>
                </th>
                <th scope="col">{headerTitle}</th>
                <th scope="col">
                  <abbr title={tooltipExtId}>{headerExtId}</abbr>
                </th>
                <th scope="col">{headerActive}</th>
              </tr>
            </thead>
            <tbody>
              {
                primaryBillingPoints
                && primaryBillingPoints.edges
                && primaryBillingPoints.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    uuid, externalIdentifier, identifier, property, active,
                  } = node;

                  return (
                    <tr key={`admin-primaryBillingPoint-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={3}>
                        <Link to={`/admin/primary-billing-points/${uuid}`}>
                          {identifier}
                        </Link>
                        <br />
                        <UUID uuid={uuid} />
                        <br />
                        {externalIdentifier}
                      </th>
                      {property && (
                        <>
                          <td colSpan={2}>
                            <Link to={`/admin/properties/${property.uuid}`}>
                              {property.title}
                            </Link>
                            <br />
                            {property.externalIdentifier}
                            <br />
                            <UUID uuid={property.uuid} />
                          </td>
                          <td>
                            <BadgeActiveTimeRange active={property.active} />
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminPrimaryBillingPointList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminPrimaryBillingPointList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminPrimaryBillingPointList,
  {
    viewer: graphql`
      fragment AdminPrimaryBillingPointList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        primaryBillingPoints(
          first: $count
          after: $cursor
        ) @connection(key: "AdminPrimaryBillingPointList_primaryBillingPoints") {
          edges {
            cursor
            node {
              uuid
              externalIdentifier
              identifier
              active {
                start
                finish
              }
              property {
                uuid
                externalIdentifier
                title
                active {
                  start
                  finish
                }
              }
            }
          }
          count
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.primaryBillingPoints;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminPrimaryBillingPointList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminPrimaryBillingPointList_viewer
      }
    }
  `,
  },
);
